import * as React from 'react';
import { styled } from '@mui/system';
import TextField from '@mui/material/TextField';
import isEqual from 'lodash/isEqual';
import _ from 'lodash';
import Button from '@mui/material/Button';
import axios, { type Method } from 'axios';
import store from '../../../../store/store';
import * as helper from '../../../../helper/index';
import LinearProgress from '@mui/material/LinearProgress';
import { toastr } from 'react-redux-toastr';
import { useParams } from 'react-router-dom';

const grey = {
  50: '#F3F6F9',
  100: '#E7EBF0',
  200: '#E0E3E7',
  300: '#CDD2D7',
  400: '#B2BAC2',
  500: '#A0AAB4',
  600: '#6F7E8C',
  700: '#3E5060',
  800: '#2D3843',
  900: '#1A2027',
};

const Root = styled('div')(
  ({ theme }) => `
    table {
      font-family: IBM Plex Sans, sans-serif;
      font-size: 0.875rem;
      border-collapse: collapse;
      width: 100%;
    }
  
    td,
    th {
      border: 1px solid ${theme.palette.mode === 'dark' ? grey[800] : grey[200]};
      text-align: left;
      padding: 6px;
    }
  
    th {
      background-color: ${theme.palette.mode === 'dark' ? grey[900] : grey[100]};
    }
    `,
);

export default function UnstyledTable() {
  // eslint-disable-next-line
  let [get_column_weight, set_column_weight]: [number[], any] = React.useState([1, 2, 3, 4, 5]);
  // eslint-disable-next-line
  let [get_row_size, set_row_size]: [string[], any] = React.useState(['small']);
  const [const_price, set_const_price]: any = React.useState({});
  const [get_price, set_price]: any = React.useState({});
  const [button_update, set_button_update]: any = React.useState(false);
  const [configuration_id, set_configuration_id] = React.useState(null);
  const [loading, setLoading]: any = React.useState(false);

  const { id }: any = useParams();

  React.useEffect(() => {
    setLoading(true);

    const url = id
      ? `${process.env.REACT_APP_API_SERVER}/delivery-charges?company=${id}`
      : `${process.env.REACT_APP_API_SERVER}/configurations?key=delivery_charge`;

    axios
      .get(url, {
        headers: {
          Authorization: `Bearer ${store.getState().auth.jwt}`,
        },
      })
      .then((res) => {
        set_configuration_id(_.get(_.head(res.data), 'id', null));
        set_const_price({ ...(_.get(_.head(res.data), 'value', {}) ?? {}) });
        set_price({ ...(_.get(_.head(res.data), 'value', {}) ?? {}) });
      })
      .catch((e) => {
        helper.axiosErrorHandling(e);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  async function updatePrice() {
    setLoading(true);

    let method: Method = 'put';
    let url = `${process.env.REACT_APP_API_SERVER}/configurations/${configuration_id}`;
    const data: any = {};
    if (id) {
      if (configuration_id) {
        method = 'put';
        url = `${process.env.REACT_APP_API_SERVER}/delivery-charges/${configuration_id}`;
      } else {
        method = 'post';
        url = `${process.env.REACT_APP_API_SERVER}/delivery-charges`;
        data.company = id;
      }
    }

    await axios({
      method,
      url,
      data: { value: get_price, ...data },
      headers: {
        Authorization: `Bearer ${store.getState().auth.jwt}`,
      },
    })
      .then((res) => {
        set_button_update(false);
        set_const_price({ ...(_.get(res.data, 'value', {}) ?? {}) });
        set_price({ ...(_.get(res.data, 'value', {}) ?? {}) });
        set_configuration_id(_.get(res.data, 'id', null));
        toastr.success('Price Updated', '');
      })
      .catch((e) => {
        helper.axiosErrorHandling(e);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <Root sx={{ maxWidth: '100%' }}>
      {!loading && (
        <div>
          <table aria-label="custom pagination table">
            <tbody>
              {get_column_weight.map((weight, w) => {
                return w === 0 ? (
                  <React.Fragment>
                    <tr>
                      <td>{'Weight/Size'}</td>
                      {get_row_size.map((size, s) => {
                        return <td>{size}</td>;
                      })}
                    </tr>
                    <tr>
                      <td>{weight}kg.</td>
                      {get_row_size.map((size, s) => {
                        return (
                          <td>
                            <TextField
                              type="number"
                              label={weight + 'kg. ' + size}
                              variant="outlined"
                              defaultValue={const_price[`${size + weight}`]}
                              onChange={(e) => {
                                get_price[`${size + weight}`] = Number(e.target.value);
                                set_price(get_price);
                                set_button_update(!isEqual(const_price, get_price));
                              }}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  </React.Fragment>
                ) : (
                  <tr>
                    <td>{weight}kg.</td>
                    {get_row_size.map((size, s) => {
                      return (
                        <td>
                          <TextField
                            type="number"
                            label={weight + 'kg. ' + size}
                            variant="outlined"
                            defaultValue={const_price[`${size + weight}`]}
                            onChange={(e) => {
                              get_price[`${size + weight}`] = Number(e.target.value);
                              set_price(get_price);
                              set_button_update(!isEqual(const_price, get_price));
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Button disabled={!button_update} variant="contained" onClick={updatePrice}>
            Update
          </Button>
        </div>
      )}
      {loading && <LinearProgress />}
    </Root>
  );
}
